import { useRef } from 'react';

let counter = 0;

/**
 * Id del `<label>` que FormField renderiza para un campo con `id` explícito.
 * Lo exponemos porque los hijos que no son elementos etiquetables (p.ej. el
 * `contenteditable` de Froala, al que `<label for>` no alcanza) necesitan
 * apuntar a ese label con `aria-labelledby` sin repetir la convención a mano.
 */
export function getFormFieldLabelId(id: string): string {
  return `${id}-label`;
}

type FormFieldAriaOptions = {
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  id?: string;
  success?: boolean;
  successMessage?: string;
};

type FormFieldAriaIds = {
  baseId: string;
  helperId: string;
  labelId: string;
  validationId: string;
};

type FormFieldAriaResult = {
  ariaDescribedBy: string | undefined;
  ids: FormFieldAriaIds;
};

function useFormFieldAria({
  error,
  errorMessage,
  helperText,
  id,
  success,
  successMessage,
}: FormFieldAriaOptions): FormFieldAriaResult {
  const idsRef = useRef<FormFieldAriaIds | null>(null);

  if (idsRef.current === null) {
    counter += 1;
    const base = id ?? `field-${counter}`;
    idsRef.current = {
      baseId: base,
      helperId: `${base}-helper`,
      labelId: getFormFieldLabelId(base),
      validationId: `${base}-validation`,
    };
  }

  const ids = idsRef.current;
  const hasValidationMessage = (!!error && !!errorMessage) || (!!success && !!successMessage && !error);

  const ariaDescribedBy =
    [helperText ? ids.helperId : null, hasValidationMessage ? ids.validationId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return { ariaDescribedBy, ids };
}

export default useFormFieldAria;
